'use client';

import { memo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
  AlertTriangle,
  Check,
  CheckCircle,
  Loader2,
  EyeOff,
  Pencil,
  PenLine,
  SquareDashedMousePointer,
  X
} from 'lucide-react';

import { cn, hexToRgba } from '@/lib/utils';
import { ColorPickerPopover } from '@/components/color-picker-popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { resolveFieldLabel } from '../utils/field-label';
import { isFieldGrounded } from '../utils/field-grounding';
import type { ExtractedDataItem } from '../types';

/**
 * Ceiling for a field NAME, mirroring `RenameFieldRequest`'s `max:255` so the
 * input stops where the endpoint does instead of 422-ing on a name the
 * operator has already finished typing.
 */
const FIELD_NAME_MAX = 255;

// ── Confidence ring: animated SVG circle ──
function ConfidenceRing({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const r = 11;
  const c = 2 * Math.PI * r;
  const offset = c - value * c;

  return (
    <div
      className={cn(
        'relative h-7 w-7 shrink-0',
        value >= 0.95
          ? 'text-emerald-500'
          : value >= 0.8
            ? 'text-blue-500'
            : 'text-red-500'
      )}
    >
      <svg viewBox='0 0 26 26' className='-rotate-90'>
        <circle
          cx='13'
          cy='13'
          r={r}
          fill='none'
          stroke='currentColor'
          strokeOpacity={0.15}
          strokeWidth={2.5}
        />
        <circle
          cx='13'
          cy='13'
          r={r}
          fill='none'
          stroke='currentColor'
          strokeWidth={2.5}
          strokeLinecap='round'
          strokeDasharray={c}
          strokeDashoffset={offset}
          className='transition-all duration-700'
        />
      </svg>
      <span className='absolute inset-0 flex items-center justify-center text-[8px] font-bold'>
        {pct}
      </span>
    </div>
  );
}

interface ExtractedFieldCardProps {
  field: ExtractedDataItem;
  isActive: boolean;
  isHighlighted?: boolean;
  highlightsEnabled?: boolean;
  readOnly?: boolean;
  // Exposes the edit affordance on already-verified fields too. Used by the QA
  // detail pages so a reviewer can correct an accepted value; off everywhere
  // else (a verified field is otherwise final).
  allowEditVerified?: boolean;
  // True while this field's accept request is in flight — shows a spinner in
  // place of the accept action so the operator gets immediate feedback.
  isAccepting?: boolean;
  // `ref` is `field.references[0] ?? ''`. It exists because field `key` is not
  // unique across pages — the same `custom_7` may appear on PG-001, PG-002,
  // and PG-003. Pairing it with the first chunk reference disambiguates.
  onHover: (fieldKey: string | null, ref: string) => void;
  onClick: (fieldKey: string, ref: string) => void;
  // `pageNumber` is set on fields from repeating templates (same key once
  // per page) so the mutation targets the right page's record.
  onAccept: (fieldKey: string, pageNumber?: number | null) => void;
  onUpdate: (
    fieldKey: string,
    value: string,
    pageNumber?: number | null
  ) => void;
  /**
   * Effective highlight color (per-document override → template color →
   * default setting). Drives this card's tint and the matching viewer boxes.
   */
  highlightColor?: string | null;
  /** True when the color shown comes from a per-document override. */
  hasColorOverride?: boolean;
  /**
   * Recolor this field for THIS document only (`null` clears the override).
   * Absent means the swatch is hidden — the color is read-only here.
   */
  onColorChange?: (fieldKey: string, color: string | null) => void;
  /**
   * Rename this field (OMA-631) — its NAME, never its value. Absent means the
   * affordance is hidden, which is how every read-only surface (QA, the
   * supervisor's review) renders the card: correcting what a field is CALLED
   * is an operator edit, and a reviewer decides about values.
   *
   * A rejected promise leaves the editor open with what the operator typed:
   * the server refuses a name that folds to nothing or that another field on
   * the same page already carries, and retyping it would be the operator
   * paying for the server's answer.
   */
  onRename?: (
    fieldKey: string,
    name: string,
    pageNumber?: number | null
  ) => Promise<unknown>;
  /**
   * Toggle draw-a-highlight mode for this field (OMA-592). A field may carry
   * several regions, so this is offered whether or not it already has one;
   * removing a region is done from the box's own right-click menu. Absent
   * means the affordance is hidden.
   */
  onAddHighlight?: (fieldKey: string, pageNumber?: number | null) => void;
  /** True while this field is the one being drawn for. */
  isDrawingHighlight?: boolean;
}

// `memo` so that hovering one field doesn't re-render the other 12+. When
// the parent's `hoveredField` flips, only the card entering and the card
// leaving see new `isHighlighted` props — every other card's props are
// reference-equal and skip the render entirely.
export const ExtractedFieldCard = memo(ExtractedFieldCardImpl);

function ExtractedFieldCardImpl({
  field,
  isActive,
  isHighlighted,
  highlightsEnabled = true,
  readOnly = false,
  allowEditVerified = false,
  isAccepting = false,
  onHover,
  onClick,
  onAccept,
  onUpdate,
  highlightColor,
  hasColorOverride = false,
  onColorChange,
  onRename,
  onAddHighlight,
  isDrawingHighlight = false
}: ExtractedFieldCardProps) {
  const t = useTranslations('ocr');
  const locale = useLocale();
  const fieldLabel = resolveFieldLabel(field, locale);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(field.value ?? ''));
  // The NAME editor is its own state, not a mode of the value editor: the
  // ticket asks for the two to be independently editable, and one shared flag
  // would make correcting a name open the value textarea beside it.
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(fieldLabel);
  const [renameSaving, setRenameSaving] = useState(false);

  const hasAlert = field.alerts && field.alerts.length > 0;
  const hasRule = field.rule && !field.rule.is_applied;
  const confidenceRate = field.confidence_rate ?? 0;
  const isVerified = field.status === 'processed';
  const isGrounded = isFieldGrounded(field);

  function handleSave() {
    onUpdate(field.key, editValue, field.page_number);
    setEditing(false);
  }

  function handleCancel() {
    setEditValue(String(field.value ?? ''));
    setEditing(false);
  }

  function startRenaming() {
    // Seeded from the RENDERED label, which is what the operator is correcting:
    // `field.label` is empty on a template field, whose name comes from the
    // template, and on one the model never labelled.
    setRenameValue(fieldLabel);
    setRenaming(true);
  }

  function cancelRenaming() {
    setRenameValue(fieldLabel);
    setRenaming(false);
  }

  async function handleRenameSave() {
    if (!onRename) return;

    const name = renameValue.trim();

    // Nothing to save is not an error: an operator who opened the editor and
    // changed their mind gets it closed, not a validation message.
    if (name === '' || name === fieldLabel) {
      cancelRenaming();
      return;
    }

    setRenameSaving(true);
    try {
      await onRename(field.key, name, field.page_number);
      setRenaming(false);
    } catch {
      // Surfaced by the global MutationCache toast. The editor stays open with
      // what was typed, so the operator adjusts it instead of retyping it.
    } finally {
      setRenameSaving(false);
    }
  }

  // Effective color = per-document override → template color → default
  // setting. Falls back to `field.color` when the parent doesn't resolve one.
  const effectiveColor = highlightColor ?? field.color ?? null;

  // The field's color takes over the active/highlighted visual weight
  // (mirroring the red/blue ring+tint below) instead of the default classes.
  // Idle rows never tint permanently, even when the field has a color set.
  const colorStyle =
    (isActive || isHighlighted) && effectiveColor
      ? {
          borderColor: hexToRgba(effectiveColor, 0.5),
          backgroundColor: hexToRgba(effectiveColor, isActive ? 0.12 : 0.08),
          boxShadow: `0 0 0 ${isActive ? '2px' : '1px'} ${hexToRgba(effectiveColor, 0.3)}`
        }
      : undefined;
  const hasCustomColor = colorStyle != null;

  return (
    <div
      // The panel scrolls to the ACTIVE card by querying this attribute. A ref
      // map keyed by (result, key, chunk-ref) was the first attempt and missed:
      // the card's field object and the one `handleChunkClick` resolves come
      // from different arrays, so any key built from their contents can drift.
      // The DOM says which card is active without a second source of truth.
      data-field-active={isActive ? 'true' : undefined}
      className={cn(
        'group cursor-pointer rounded-lg border p-3',
        // Targeted transitions only (colors + ring/shadow). `transition-all`
        // forces every animatable property to transition on every paint and
        // is the lazy default; the change here is purely color/border/ring.
        'transition-[background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
        !hasCustomColor &&
          (isActive
            ? 'border-red-500/50 bg-red-50/50 ring-2 ring-red-500/30 dark:bg-red-950/20'
            : isHighlighted
              ? 'border-blue-500/50 bg-blue-50/50 ring-1 ring-blue-500/30 dark:bg-blue-950/20'
              : 'hover:border-muted-foreground/20 hover:bg-muted/30'),
        isVerified &&
          !isActive &&
          !isHighlighted &&
          'border-emerald-500/30 bg-emerald-50/30 dark:bg-emerald-950/10',
        hasRule &&
          !isVerified &&
          !isActive &&
          !isHighlighted &&
          'border-destructive/40',
        hasAlert &&
          !isVerified &&
          !hasRule &&
          !isActive &&
          !isHighlighted &&
          'border-amber-500/30'
      )}
      style={colorStyle}
      onMouseEnter={() => onHover(field.key, field.references[0] ?? '')}
      // No onMouseLeave: clearing hover here makes the viewer flicker back to
      // the active page every time the mouse crosses the gap between two
      // cards on the same OTHER page. The parent panel clears hover on its
      // own pointerleave instead.
      onClick={() => onClick(field.key, field.references[0] ?? '')}
    >
      <div className='flex items-start gap-2.5'>
        {/* Confidence ring */}
        {field.confidence_rate != null && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='mt-0.5'>
                <ConfidenceRing value={confidenceRate} />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {confidenceRate >= 0.95
                  ? t('confidenceHigh')
                  : confidenceRate >= 0.8
                    ? t('confidenceMedium')
                    : t('confidenceLow')}
              </p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Content */}
        <div className='min-w-0 flex-1'>
          <div className='flex items-center justify-between gap-2'>
            {renaming ? (
              <div
                className='flex min-w-0 flex-1 items-center gap-1'
                onClick={(e) => e.stopPropagation()}
              >
                {/* The name is read off the DOCUMENT, so it carries the
                    document's direction, not the interface's — the same rule
                    the value and the add-field dialog follow. */}
                <Input
                  dir='auto'
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  maxLength={FIELD_NAME_MAX}
                  disabled={renameSaving}
                  autoFocus
                  aria-label={t('renameFieldLabel')}
                  className='h-7 min-w-0 flex-1 text-xs'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleRenameSave();
                    }
                    if (e.key === 'Escape') cancelRenaming();
                  }}
                />
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-6 w-6 shrink-0'
                  title={t('renameFieldSave')}
                  aria-label={t('renameFieldSave')}
                  disabled={renameSaving}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRenameSave();
                  }}
                >
                  {renameSaving ? (
                    <Loader2 className='h-3 w-3 animate-spin' />
                  ) : (
                    <Check className='h-3 w-3' />
                  )}
                </Button>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-6 w-6 shrink-0'
                  title={t('cancel')}
                  aria-label={t('cancel')}
                  disabled={renameSaving}
                  onClick={(e) => {
                    e.stopPropagation();
                    cancelRenaming();
                  }}
                >
                  <X className='h-3 w-3' />
                </Button>
              </div>
            ) : (
              <span
                className='text-muted-foreground min-w-0 flex-1 truncate text-[11px] font-medium uppercase'
                title={fieldLabel}
              >
                {fieldLabel}
              </span>
            )}

            {/* Rename (OMA-631). Beside the name rather than among the value
                actions on the right, because it edits what sits to its left —
                and it stays available on a VERIFIED field, since accepting a
                value says nothing about whether the field is named right. */}
            {onRename && !renaming && !readOnly && (
              <Button
                size='icon'
                variant='ghost'
                // Always visible on touch, reveal-on-hover from `sm` up — the
                // same rule the value actions follow.
                className='h-6 w-6 shrink-0 transition-opacity sm:opacity-0 sm:group-hover:opacity-100'
                title={t('renameField')}
                aria-label={t('renameField')}
                onClick={(e) => {
                  e.stopPropagation();
                  startRenaming();
                }}
              >
                <PenLine className='h-3 w-3' />
              </Button>
            )}

            {/* OMA-614 — this field is not on the template; the label came
                from the model reading the page, not from a field definition. */}
            {/* OMA-668 — a field an operator added is ALSO `freeform`, so it
                gets its own badge rather than reading as a model discovery. */}
            {field.source === 'freeform' && field.origin === 'manual' ? (
              <Badge
                variant='outline'
                className='shrink-0 border-sky-500/40 text-[10px] text-sky-600 uppercase'
              >
                {t('manualBadge')}
              </Badge>
            ) : (
              field.source === 'freeform' && (
                <Badge
                  variant='outline'
                  className='text-muted-foreground shrink-0 text-[10px] uppercase'
                >
                  {t('freeformBadge')}
                </Badge>
              )
            )}

            {/* A value with no position can only be accepted on trust — there
                is nothing in the document to compare it against. Flagged on
                every surface that renders this card, QA1 included, so a
                reviewer can tell a checked value from a taken-on-faith one. */}
            {!isGrounded && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant='outline'
                    className='shrink-0 border-amber-500/30 bg-amber-500/10 text-[10px] text-amber-600'
                  >
                    <EyeOff className='me-0.5 h-2.5 w-2.5' />
                    {t('ungroundedBadge')}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className='max-w-xs'>
                  <p>{t('ungroundedBadgeHint')}</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Per-document highlight color. Recolors this field on THIS
                container only (viewer boxes, this card, and the highlighted
                PDF export) — the OCR template's color is never touched. */}
            {onColorChange && highlightsEnabled && !readOnly && (
              // Stops the card's own click (which activates the field and
              // scrolls the viewer) from firing when picking a color.
              <span
                className='shrink-0'
                onClick={(e) => e.stopPropagation()}
                role='presentation'
              >
                <ColorPickerPopover
                  value={effectiveColor}
                  onChange={(hex) => onColorChange(field.key, hex)}
                  onReset={
                    hasColorOverride
                      ? () => onColorChange(field.key, null)
                      : undefined
                  }
                  resetLabel={t('resetFieldHighlightColor')}
                  align='end'
                  trigger={
                    <Button
                      type='button'
                      size='icon'
                      variant='ghost'
                      className='h-6 w-6'
                      title={t('fieldHighlightColor')}
                      aria-label={t('fieldHighlightColor')}
                    >
                      <span
                        className={cn(
                          'h-3.5 w-3.5 rounded-full border',
                          hasColorOverride && 'ring-primary/60 ring-2'
                        )}
                        style={
                          effectiveColor
                            ? { backgroundColor: effectiveColor }
                            : undefined
                        }
                      />
                    </Button>
                  }
                />
              </span>
            )}

            {/* Draw a highlight for this field (OMA-592). Offered on every
                field, not only refless ones: a value can span several regions,
                and the pipeline routinely boxes only part of a wrapped line.
                Toggles draw mode, so the button reads as pressed while armed. */}
            {onAddHighlight && highlightsEnabled && !readOnly && (
              <Button
                size='icon'
                variant='ghost'
                className={cn(
                  'h-6 w-6 shrink-0',
                  isDrawingHighlight && 'text-primary bg-primary/10'
                )}
                title={t('addHighlight')}
                aria-label={t('addHighlight')}
                aria-pressed={isDrawingHighlight}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddHighlight(field.key, field.page_number);
                }}
              >
                <SquareDashedMousePointer className='h-3.5 w-3.5' />
              </Button>
            )}

            {/* Right side: in-flight spinner, status badge, or action buttons */}
            {isAccepting && !readOnly ? (
              <span className='inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-emerald-600'>
                <Loader2 className='h-3 w-3 animate-spin' />
                {t('verifying')}
              </span>
            ) : isVerified && !readOnly ? (
              <div className='flex shrink-0 items-center gap-0.5'>
                <Badge
                  variant='outline'
                  className='border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-600'
                >
                  <CheckCircle className='me-0.5 h-2.5 w-2.5' />
                  {t('statusVerified')}
                </Badge>
                {allowEditVerified && (
                  <Button
                    size='icon'
                    variant='ghost'
                    // Always visible on touch (no hover there), reveal-on-hover
                    // from `sm` up — same rule as the unverified actions below.
                    className='h-6 w-6 transition-opacity sm:opacity-0 sm:group-hover:opacity-100'
                    title={t('editVerifiedField')}
                    aria-label={t('editVerifiedField')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(true);
                    }}
                  >
                    <Pencil className='h-3 w-3' />
                  </Button>
                )}
              </div>
            ) : !isVerified && !readOnly ? (
              <div className='flex shrink-0 items-center gap-0.5 transition-opacity sm:opacity-0 sm:group-hover:opacity-100'>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-6 w-6'
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(true);
                  }}
                >
                  <Pencil className='h-3 w-3' />
                </Button>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-6 w-6 text-emerald-600 hover:text-emerald-700'
                  onClick={(e) => {
                    e.stopPropagation();
                    onAccept(field.key, field.page_number);
                  }}
                >
                  <Check className='h-3 w-3' />
                </Button>
              </div>
            ) : null}
          </div>

          {/* Value (view or edit mode) */}
          {editing ? (
            <div className='mt-1.5 space-y-1.5'>
              <div className='flex items-start gap-1.5'>
                {/* A textarea, not an input: extracted values are often whole
                    address blocks / multi-line paragraphs that an operator has
                    to read while correcting. Enter therefore inserts a newline
                    and Ctrl/Cmd+Enter saves (Escape still cancels). */}
                <Textarea
                  // Same reason as the value paragraph: an operator has to read
                  // the value in the document's own direction while fixing it.
                  dir='auto'
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className='min-h-16 min-w-0 flex-1 text-sm'
                  rows={3}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSave();
                    }
                    if (e.key === 'Escape') handleCancel();
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className='flex shrink-0 flex-col gap-0.5'>
                  <Button
                    size='icon'
                    variant='ghost'
                    className='h-7 w-7'
                    title={t('saveFieldHint')}
                    aria-label={t('saveFieldHint')}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                  >
                    <Check className='h-3.5 w-3.5' />
                  </Button>
                  <Button
                    size='icon'
                    variant='ghost'
                    className='h-7 w-7'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                  >
                    <X className='h-3.5 w-3.5' />
                  </Button>
                </div>
              </div>
              <p className='text-muted-foreground text-[10px]'>
                {t('editFieldKeyboardHint')}
              </p>
            </div>
          ) : (
            // `whitespace-pre-wrap` so a multi-line corrected value reads back
            // the way it was typed instead of collapsing onto one line.
            //
            // `dir='auto'` because the value is in the document's language, not
            // the UI's: inheriting the Arabic shell's RTL direction moves a
            // leading numeric run to the far end of the line, so a correct
            // "90 days from invoice date" reads back as
            // "days from invoice date 90" and a correct "187 345.90" total reads
            // back as "345.90 187". `plaintext` resolves each line of a wrapped
            // address block on its own instead of following line 1.
            <p
              dir='auto'
              className='mt-0.5 text-sm font-medium break-words whitespace-pre-wrap [unicode-bidi:plaintext]'
            >
              {field.value != null ? (
                String(field.value)
              ) : (
                <span className='text-muted-foreground italic'>
                  {t('noValue')}
                </span>
              )}
            </p>
          )}

          {/* Rule / validation alerts */}
          {hasAlert && (
            <div className='mt-1.5 space-y-0.5'>
              {field.alerts.map((alert, i) => (
                <div key={i} className='flex items-start gap-1'>
                  <AlertTriangle className='text-destructive mt-0.5 h-3 w-3 shrink-0' />
                  <p className='text-destructive min-w-0 flex-1 text-xs break-words'>
                    {alert}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
